import { onMount, onCleanup, createEffect } from "solid-js";

/**
 * canvas-confetti renders via a single GPU canvas (optionally an OffscreenCanvas
 * inside a Worker on supporting platforms). 3.2 KB gz vs 90 DOM spans + animated
 * box-shadow that used to cost ~12 ms paint on iPhone SE.
 *
 * The library exposes a singleton `confetti(opts)`. For the burst we trigger
 * three staggered shots, gives a satisfying "shower" effect for the jackpot.
 */
interface ConfettiProps {
  /** Bump the counter to fire a new burst. */
  trigger: number;
  intensity?: "small" | "burst";
}

export function Confetti(props: ConfettiProps) {
  let prev = 0;
  let confettiFn: ((opts: any) => void) | null = null;

  onMount(async () => {
    try {
      const mod = await import("canvas-confetti");
      confettiFn = mod.default;
    } catch (err) {
      console.warn("canvas-confetti load failed", err);
    }
  });

  createEffect(() => {
    const trig = props.trigger;
    if (!trig || trig === prev) return;
    prev = trig;
    if (!confettiFn) return;

    const gold = ["#e8d4a2", "#c9a961", "#b8923f", "#fff5d0", "#f4e5b8", "#d4b56e"];

    if (props.intensity === "burst") {
      // Three-shot showers from left / centre / right.
      const origins = [
        { x: 0.2, y: 0.7 },
        { x: 0.5, y: 0.7 },
        { x: 0.8, y: 0.7 },
      ];
      origins.forEach((o, i) =>
        setTimeout(() => {
          confettiFn!({
            particleCount: 120,
            spread: 90,
            startVelocity: 45,
            ticks: 240,
            scalar: 1.05,
            colors: gold,
            origin: o,
            disableForReducedMotion: true,
            useWorker: true,
          });
        }, i * 180),
      );
    } else {
      confettiFn({
        particleCount: 50,
        spread: 60,
        startVelocity: 38,
        ticks: 180,
        scalar: 0.85,
        colors: gold,
        origin: { x: 0.5, y: 0.65 },
        disableForReducedMotion: true,
        useWorker: true,
      });
    }
  });

  onCleanup(() => {
    // No DOM nodes owned by us; the library tears its own canvas down lazily.
  });

  return null;
}
